import { BusinessObjectBase } from './base';
import { horseGroupMemberSchema } from '@schemas/business-objects/horse-group-member';
import type { Horse } from './horse';
import type { HorseGroup } from './horse-group';
import type { Id } from '../base-types';

export interface HorseGroupMemberData {
  readonly id: Id;
  readonly legacyId?: number;
  readonly horseGroupId: Id;
  readonly horseGroup?: HorseGroup;
  readonly horseId: Id;
  readonly horse?: Horse;
}

export interface HorseGroupMember extends Readonly<HorseGroupMemberData> {}
type HorseGroupMemberCreateWithChildrenData = Partial<HorseGroupMemberData>;

export class HorseGroupMember extends BusinessObjectBase {
  static readonly entityName = 'HorseGroupMember' as const;
  static readonly plural = 'horse-group-members';
  static schema = horseGroupMemberSchema;

  // --- Static CRUD methods ---

  static async find(
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<HorseGroupMember[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params, undefined, options);
    return results.map((data) => new HorseGroupMember(data));
  }

  static async findOne(
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<HorseGroupMember | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params, undefined, options);
    return data ? new HorseGroupMember(data) : null;
  }

  static async findById(
    id: Id,
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<HorseGroupMember> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params, undefined, options);
    return new HorseGroupMember(data);
  }

  static async create(
    data: Omit<HorseGroupMemberData, 'id'>,
  ): Promise<HorseGroupMember> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new HorseGroupMember(result);
  }

  static async createMany(
    data: Omit<HorseGroupMemberData, 'id'>[],
  ): Promise<HorseGroupMember[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new HorseGroupMember(item));
  }

  static async createWithChildren(
    data: HorseGroupMemberCreateWithChildrenData,
  ): Promise<HorseGroupMember> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new HorseGroupMember(result);
  }

  static async createManyWithChildren(
    data: HorseGroupMemberCreateWithChildrenData[],
  ): Promise<HorseGroupMember[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new HorseGroupMember(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<HorseGroupMemberData>,
  ): Promise<HorseGroupMember> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new HorseGroupMember(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
